import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Mic, Loader2, Play, ExternalLink, Youtube, Headphones
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PodcastPage = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEpisode, setSelectedEpisode] = useState(null);

  useEffect(() => {
    fetchEpisodes();
  }, []);

  const fetchEpisodes = async () => {
    try {
      const response = await axios.get(`${API_URL}/podcast`);
      setEpisodes(response.data);
    } catch (error) {
      console.error('Failed to fetch podcast episodes:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="podcast-page">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 mb-2">Illuminate Your Worth Podcast</h1>
          <p className="text-stone-600">Conversations about transformation, worth, and living authentically</p>
        </div>
        
        {episodes.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-stone-500 bg-stone-100 px-3 py-1.5 rounded-full">
            <Youtube className="w-4 h-4 text-red-500" />
            <span>{episodes.length} episodes</span>
          </div>
        )}
      </div>

      {episodes.length === 0 ? (
        <div className="card-cosmic p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-6">
            <Headphones className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-3">Coming Soon!</h2>
          <p className="text-stone-600 max-w-md mx-auto">
            The Illuminate Your Worth podcast is launching soon. 
            Stay tuned for inspiring conversations about transformation and self-worth.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Featured Episode / Player */}
          {selectedEpisode && (
            <div className="card-cosmic p-6 bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="lg:w-80 flex-shrink-0">
                  <div className="aspect-video rounded-xl overflow-hidden bg-stone-200">
                    <iframe
                      src={`https://www.youtube.com/embed/${selectedEpisode.video_id}`}
                      className="w-full h-full"
                      title={selectedEpisode.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-stone-900 mb-2">{selectedEpisode.title}</h2>
                  <p className="text-stone-600 text-sm mb-4 line-clamp-3">{selectedEpisode.description}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(selectedEpisode.video_url, '_blank')}
                  >
                    <Youtube className="w-4 h-4 mr-2 text-red-500" />
                    Watch on YouTube
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Episode Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {episodes.map((episode) => (
              <div 
                key={episode.id} 
                className={`card-cosmic overflow-hidden cursor-pointer transition-all hover:shadow-lg ${
                  selectedEpisode?.id === episode.id ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => setSelectedEpisode(episode)}
                data-testid={`episode-card-${episode.id}`}
              >
                <div className="relative aspect-video bg-stone-200 group">
                  {episode.thumbnail_url ? (
                    <img 
                      src={episode.thumbnail_url} 
                      alt={episode.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20">
                      <Mic className="w-12 h-12 text-primary/50" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-6 h-6 text-primary ml-1" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-stone-900 mb-1 line-clamp-2">{episode.title}</h3>
                  <p className="text-sm text-stone-500 line-clamp-2">{episode.description}</p>
                  {episode.duration_minutes && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-stone-400">
                      <Headphones className="w-3 h-3" />
                      <span>{episode.duration_minutes} min</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastPage;
